export type TopicSection = {
  id: string;
  heading: string;
  paragraphs: string[];
};

export type Topic = {
  id: number;
  slug: string;
  title: string;
  category: string;
  categoryName: string;
  summary: string;
  intro: string;
  cover: string;
  count: number;
  updated: string;
  readMins: number;
  status: string;
  tags: string[];
  sections: TopicSection[];
};

export const topics: Topic[] = [
  {
    id: 1,
    slug: 'year-hot-search-review',
    title: '年度热搜大盘点：这一年我们共同关注过的瞬间',
    category: 'hotsearch',
    categoryName: '热搜盘点',
    summary: '把一整年冲上过热榜的话题按季度梳理，看情绪如何一波一波地来，又一次一次地散。',
    intro:
      '每年都会有一批话题突然占据所有人的屏幕。这份盘点不追个案的对错，只做一件事：把话题出现的时间、讨论的焦点和消散的节奏排成一条线，让你看清一年里大家的注意力都去了哪里。',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20abstract%20editorial%20collage%20with%20glowing%20amber%20and%20crimson%20heat%20waves%20over%20cream%20paper%20texture%2C%20minimal%20geometric%20shapes%2C%20soft%20cinematic%20shadows%2C%20magazine%20cover%20aesthetic%2C%20no%20text&width=800&height=600&seq=hl-topic-01&orientation=landscape',
    count: 48,
    updated: '2026-08-28',
    readMins: 12,
    status: '持续更新',
    tags: ['年度盘点', '热搜', '注意力'],
    sections: [
      {
        id: 's1',
        heading: '第一季度：从围观到参与',
        paragraphs: [
          '年初的话题往往带着一点节日的余温，讨论多是轻松的、分享式的。人们更愿意把一个有趣的片段转发给朋友，而不是展开辩论。',
          '这个阶段的特征是参与门槛低：一条短视频、一句顺口的梗，就能让话题在两三天内铺满首页。我们把这一季的条目按讨论量排序，会发现排在前面的几乎都是可被模仿的内容。',
        ],
      },
      {
        id: 's2',
        heading: '第二季度：观点开始分化',
        paragraphs: [
          '进入春夏之交，话题的形态变了。同一个事件开始出现两条并行的叙事线，评论区从玩梗转向表态，讨论的周期也从两天拉长到一周以上。',
          '值得注意的是，这一阶段被记住的往往不是事件本身，而是围绕它形成的一批说法。盘点里我们把每条话题的核心争议单独摘出来，方便你对比不同立场的表达方式。',
        ],
      },
      {
        id: 's3',
        heading: '下半年：注意力回归生活',
        paragraphs: [
          '到了下半年，热榜的构成明显更贴近日常：消费、出行、健康、职场。大起大落的话题变少了，取而代之的是能持续很久的小趋势。',
          '这未必是坏事。当讨论从情绪转向具体经验，内容才真正沉淀下来。这份盘点的最后一部分，就是我们挑出的几条「慢热但有用」的话题线。',
        ],
      },
    ],
  },
  {
    id: 2,
    slug: 'variety-show-golden-moments',
    title: '综艺名场面收藏夹：那些反复被二创的高光时刻',
    category: 'entertain',
    categoryName: '娱乐观察',
    summary: '不评价节目好坏，只看一个片段为什么会被剪上千次，成为大家共同的记忆锚点。',
    intro:
      '一个画面能被称为「名场面」，靠的不是播出时的热度，而是被反复重剪、重新配乐、二次演绎的能力。这期专题我们拆解这些片段的结构，看它们凭什么留下来。',
    cover:
      'https://readdy.ai/api/search-image?query=Retro%20stage%20spotlight%20with%20golden%20dust%20particles%20above%20warm%20cream%20backdrop%2C%20abstract%20theater%20curtain%20shapes%20in%20crimson%2C%20editorial%20magazine%20art%2C%20soft%20cinematic%20lighting%2C%20no%20text&width=800&height=600&seq=hl-topic-02&orientation=landscape',
    count: 36,
    updated: '2026-08-21',
    readMins: 9,
    status: '持续更新',
    tags: ['综艺', '名场面', '二次创作'],
    sections: [
      {
        id: 's1',
        heading: '为什么有些片段特别经剪',
        paragraphs: [
          '可被重剪的片段通常有一个清晰的情绪拐点：从平静到爆发，或者从紧张到松弛。拐点越明显，剪辑时能发挥的空间就越大。',
          '另一个共同点是台词足够短。一句话、一个语气词就能独立成立，观众才愿意拿它当表情包或口头禅。',
        ],
      },
      {
        id: 's2',
        heading: '三种最常见的二次创作方式',
        paragraphs: [
          '第一种是换配乐，用音乐重新定义情绪；第二种是加字幕，把画面语境改成完全不同的场景；第三种是慢放与循环，把瞬间拉长成节奏。',
          '这三种方式对片段的要求各不相同。能被三种同时套用的片段，通常就是真正意义上的名场面。',
        ],
      },
      {
        id: 's3',
        heading: '收藏这些片段的意义',
        paragraphs: [
          '名场面是集体记忆的快捷方式。多年之后你可能忘了节目形式，但看到那个画面，当时的氛围会立刻回来。',
          '所以我们把这个收藏夹做成了可检索的结构：按情绪、按场景、按台词类型分好，方便你按需要取用。',
        ],
      },
    ],
  },
  {
    id: 3,
    slug: 'viral-meme-handbook',
    title: '网络热梗图鉴：从一句话到全民模仿的传播路径',
    category: 'network',
    categoryName: '网络话题',
    summary: '追一个梗从出现到消退的完整轨迹，拆开看它在哪一步真正进入了日常语言。',
    intro:
      '梗的生命周期其实很短，但少数几个会留下来，变成日常表达的一部分。这份图鉴按传播阶段排列，让你看清一个梗是在哪一步被记住的。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20speech%20bubbles%20and%20flowing%20lines%20in%20warm%20amber%20and%20sage%20green%20over%20cream%20paper%2C%20minimal%20editorial%20illustration%2C%20soft%20shadows%2C%20clean%20composition%2C%20no%20text&width=800&height=600&seq=hl-topic-03&orientation=landscape',
    count: 52,
    updated: '2026-09-03',
    readMins: 10,
    status: '持续更新',
    tags: ['热梗', '传播', '语言'],
    sections: [
      {
        id: 's1',
        heading: '第一阶段：小圈子里的黑话',
        paragraphs: [
          '几乎每个梗都诞生于一个特定的小圈子。它最初只有圈内人懂，带着一点排他性，这正是它传播的燃料。',
          '当圈外人开始好奇「这词什么意思」的时候，梗才真正开始向外扩散。',
        ],
      },
      {
        id: 's2',
        heading: '第二阶段：被简化与泛化',
        paragraphs: [
          '出圈的过程中，梗的语境会被剥离，只剩下最好记的那几个字。这是一次损耗，也是一次升级——它变得随处可用。',
          '泛化之后，原意往往已经不重要了。大家在意的是它能不能精准地表达某种情绪。',
        ],
      },
      {
        id: 's3',
        heading: '第三阶段：进入日常或彻底消失',
        paragraphs: [
          '留下来的梗会悄悄变成中性词，你甚至不觉得它是一个梗；消失的梗则会在半年内迅速变得「过时」，再被人提起时带有明显的年代感。',
          '图鉴里我们给每个梗标了当前状态，方便你判断它是正在流行，还是已经进入冷宫。',
        ],
      },
    ],
  },
  {
    id: 4,
    slug: 'blockbuster-retrospective',
    title: '爆款影视回望：现象级作品是怎么被记住的',
    category: 'scene',
    categoryName: '影像现场',
    summary: '从镜头语言到传播节点，复盘几类曾经占据讨论中心的作品留下的长期影响。',
    intro:
      '所谓现象级，不只是票房或播放量，而是它离开热搜之后还剩下什么。这份回望关注作品的余温：台词、构图、桥段如何继续被人使用。',
    cover:
      'https://readdy.ai/api/search-image?query=Cinematic%20film%20strip%20and%20projector%20light%20beam%20in%20warm%20amber%20tones%20over%20cream%20textured%20background%2C%20abstract%20editorial%20art%2C%20soft%20grain%2C%20minimal%20elegant%20composition%2C%20no%20text&width=800&height=600&seq=hl-topic-04&orientation=landscape',
    count: 29,
    updated: '2026-07-30',
    readMins: 14,
    status: '已完结',
    tags: ['影视', '镜头', '回望'],
    sections: [
      {
        id: 's1',
        heading: '被记住的往往是细节',
        paragraphs: [
          '一段时间之后还能被准确复述的，通常不是完整剧情，而是一个具体的画面：某个角度、某种光线、某句话的语气。',
          '这些细节之所以牢靠，是因为它们可以被单独拿出来，放进完全不同的语境里使用。',
        ],
      },
      {
        id: 's2',
        heading: '传播节点决定了作品的寿命',
        paragraphs: [
          '同一部作品，如果在播出初期就出现了高质量的解读和二次创作，它的关注周期会明显更长；反之则容易在两周内沉底。',
          '我们把几部代表作品的关键传播节点标了出来，可以看到讨论高峰往往出现在剧情过半之后。',
        ],
      },
      {
        id: 's3',
        heading: '余温比热度更值得记录',
        paragraphs: [
          '热度是播出期的数字，余温是几年后还有人引用的程度。后者才是作品真正留下的东西。',
          '这份回望更愿意记录余温，因为它更接近「被记住」的含义。',
        ],
      },
    ],
  },
  {
    id: 5,
    slug: 'creator-evolution',
    title: '内容创作者进化史：从图文到短视频的表达变迁',
    category: 'entertain',
    categoryName: '娱乐观察',
    summary: '梳理内容形态几次关键转向，看创作者的手艺是怎样被平台规则重新塑造的。',
    intro:
      '从长图文到一分钟视频，创作者面对的从来不只是镜头，而是一整套关于时长、节奏和开头三秒的规则。这期专题记录这些规则的变迁。',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20abstract%20timeline%20of%20ascending%20geometric%20blocks%20and%20camera%20lens%20shapes%20in%20amber%20and%20crimson%20over%20cream%20paper%2C%20minimal%20editorial%20illustration%2C%20soft%20shadows%2C%20no%20text&width=800&height=600&seq=hl-topic-05&orientation=landscape',
    count: 41,
    updated: '2026-08-15',
    readMins: 11,
    status: '持续更新',
    tags: ['创作者', '表达', '形态'],
    sections: [
      {
        id: 's1',
        heading: '第一阶段：文字主导的信息密度',
        paragraphs: [
          '早期的内容竞争的是信息密度，谁能在一篇长文里讲清一件事，谁就能获得关注。读者的耐心是那个阶段最重要的资源。',
          '这时的创作更像写作，结构的清晰度决定了读者的留存。',
        ],
      },
      {
        id: 's2',
        heading: '第二阶段：画面开始承担叙事',
        paragraphs: [
          '当视频成为主要载体，创作者需要同时处理画面和节奏。一个信息点被拆成几个镜头，留白和停顿都成了表达的一部分。',
          '这个阶段诞生的新技能是「开头设计」：如何在三秒内让观众决定不划走。',
        ],
      },
      {
        id: 's3',
        heading: '第三阶段：手艺回归内容本身',
        paragraphs: [
          '规则稳定之后，纯粹靠形式取巧的空间变小了。能长期维持关注的创作者，几乎都回到了扎实的信息与稳定的表达风格上。',
          '这对读者是好事。我们在这份盘点里挑出了几位在形态变化中始终保持水准的创作者作为样本。',
        ],
      },
    ],
  },
  {
    id: 6,
    slug: 'controversy-observation',
    title: '争议话题观察笔记：舆论场里的观点如何形成',
    category: 'network',
    categoryName: '网络话题',
    summary: '不做立场站队，只记录一个话题争议双方各自表达方式的差异与共性。',
    intro:
      '每一次争议看起来都不同，但讨论的结构往往相似：先有情绪，再有判断，最后才补上信息。这份笔记尝试把讨论的语法写下来，而不是替任何人下结论。',
    cover:
      'https://readdy.ai/api/search-image?query=Two%20opposing%20abstract%20paper%20shapes%20in%20crimson%20and%20sage%20green%20balanced%20on%20cream%20background%2C%20minimal%20editorial%20composition%2C%20soft%20natural%20lighting%2C%20quiet%20balanced%20mood%2C%20no%20text&width=800&height=600&seq=hl-topic-06&orientation=landscape',
    count: 33,
    updated: '2026-08-09',
    readMins: 8,
    status: '持续更新',
    tags: ['争议', '舆论', '表达'],
    sections: [
      {
        id: 's1',
        heading: '情绪先行是常态',
        paragraphs: [
          '大多数人在接触一个话题时，先得到的是态度而不是事实。这并不奇怪，因为带有立场的表达天生更容易传播。',
          '理解这一点，能让我们在参与讨论之前多花一分钟去补齐信息。',
        ],
      },
      {
        id: 's2',
        heading: '双方其实共用同一套语汇',
        paragraphs: [
          '有趣的是，对立的两方往往使用高度相似的关键词和句式，只是指向相反。这说明争论的焦点可能比表面看上去更小。',
          '笔记里我们把几组争议的关键词做了对照，能看到重叠的部分远比想象中多。',
        ],
      },
      {
        id: 's3',
        heading: '如何更理性地围观',
        paragraphs: [
          '三步足够：先确认信息源，再区分事实与评价，最后再决定要不要表态。做不到第三步也没关系，沉默同样是一种态度。',
          '这份笔记的目的不是让所有人达成一致，而是让讨论的成本低一点。',
        ],
      },
    ],
  },
  {
    id: 7,
    slug: 'classic-ad-creativity',
    title: '经典创意广告盘点：被反复提起的灵感瞬间',
    category: 'culture',
    categoryName: '文化盘点',
    summary: '挑出那些即使脱离了产品也依然站得住的创意，看它们好在什么地方。',
    intro:
      '好的广告创意有一个共同点：把产品拿掉之后，它依然是一个完整的作品。这份盘点按创意手法分类，不看投放量，只看想法本身。',
    cover:
      'https://readdy.ai/api/search-image?query=Playful%20abstract%20geometric%20shapes%20and%20golden%20ratio%20lines%20in%20amber%20and%20cream%2C%20minimal%20editorial%20design%20illustration%2C%20soft%20paper%20texture%2C%20clean%20balanced%20composition%2C%20no%20text&width=800&height=600&seq=hl-topic-07&orientation=landscape',
    count: 24,
    updated: '2026-06-18',
    readMins: 7,
    status: '已完结',
    tags: ['创意', '广告', '灵感'],
    sections: [
      {
        id: 's1',
        heading: '把功能翻译成感受',
        paragraphs: [
          '最容易被记住的创意，往往没有直接描述功能，而是描述使用后的感受。观众接收到的是情绪，回家后才想起产品。',
          '这类创意对洞察的要求最高，也最容易过时——一旦感受不再普遍，创意就会失效。',
        ],
      },
      {
        id: 's2',
        heading: '用一个反常识的设定',
        paragraphs: [
          '另一种常见的做法是设定一个与常识相悖的前提，让观众在几秒内产生疑问，从而停下来看完。',
          '反常识的难点在于收尾：如果结论没有回到产品，创意就变成了单纯的段子。',
        ],
      },
      {
        id: 's3',
        heading: '好创意经得起脱离语境',
        paragraphs: [
          '我们把候选作品做了同一项测试：去掉品牌信息之后，它是否依然成立。通过测试的才进入这份盘点。',
          '这算不上严谨的评审，但足够说明问题。',
        ],
      },
    ],
  },
  {
    id: 8,
    slug: 'guochao-collab',
    title: '国潮联名盘点：传统文化与新消费的碰撞',
    category: 'culture',
    categoryName: '文化盘点',
    summary: '观察一批以传统元素为灵感的联名案例，看哪些做到了形神兼备，哪些只是贴图。',
    intro:
      '国潮联名做了很多年，成功与平庸的差别越来越清晰：是借一个纹样，还是真的理解了纹样背后的用法。这份盘点按融合深度分级。',
    cover:
      'https://readdy.ai/api/search-image?query=Elegant%20traditional%20chinese%20pattern%20motifs%20in%20crimson%20and%20amber%20gold%20on%20warm%20cream%20paper%2C%20minimal%20modern%20editorial%20illustration%2C%20subtle%20texture%2C%20refined%20composition%2C%20no%20text&width=800&height=600&seq=hl-topic-08&orientation=landscape',
    count: 31,
    updated: '2026-07-11',
    readMins: 9,
    status: '持续更新',
    tags: ['国潮', '联名', '传统'],
    sections: [
      {
        id: 's1',
        heading: '第一层：纹样的直接借用',
        paragraphs: [
          '最常见的做法是把传统纹样印在产品上。成本低、见效快，但很容易显得单薄，因为纹样和产品之间没有产生新的意义。',
          '这一层的案例数量最多，我们也如实把它们单列出来。',
        ],
      },
      {
        id: 's2',
        heading: '第二层：结构与工艺的转化',
        paragraphs: [
          '更进一步的做法是把传统器物的结构或工艺逻辑，转译到现代产品的使用方式里。观众不一定认得出出处，但会觉得「对味」。',
          '做到这一层的案例通常需要更长的开发周期，这也是它们数量偏少的原因。',
        ],
      },
      {
        id: 's3',
        heading: '第三层：共享同一种审美态度',
        paragraphs: [
          '最高的一层不再依赖任何具体元素，而是整体气质与传统美学同频：留白、节奏、克制。辨识度反而最强。',
          '这份盘点最后留了一组案例，专门讨论这种「看不见的传统」。',
        ],
      },
    ],
  },
  {
    id: 9,
    slug: 'viral-video-scenes',
    title: '出圈短视频精选：镜头背后的城市与人',
    category: 'scene',
    categoryName: '影像现场',
    summary: '把被转发过百万次的短视频按拍摄地点重排，看镜头如何重新定义一座城市。',
    intro:
      '有些地方是因为一段视频而被看见的。这份精选不做旅游攻略，只记录镜头选择的角度、时间和它带来的连锁反应。',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20abstract%20city%20skyline%20silhouette%20at%20dusk%20with%20amber%20sun%20glow%20over%20cream%20gradient%2C%20minimal%20editorial%20photography%20style%2C%20soft%20layered%20shapes%2C%20no%20text&width=800&height=600&seq=hl-topic-09&orientation=landscape',
    count: 45,
    updated: '2026-08-25',
    readMins: 10,
    status: '持续更新',
    tags: ['短视频', '城市', '镜头'],
    sections: [
      {
        id: 's1',
        heading: '时间选择往往比取景更关键',
        paragraphs: [
          '同一条街，黄昏和正午呈现的是完全不同的面貌。很多出圈镜头的成功，首先来自拍摄者选对了那十几分钟。',
          '我们把入选视频的拍摄时段做了统计，清晨与黄昏占了将近七成。',
        ],
      },
      {
        id: 's2',
        heading: '一个角度带火一片区域',
        paragraphs: [
          '当某个角度被广泛模仿，它会逐渐变成这个地方的「标准照」。后来者拍出的相似画面，反而巩固了最初的印象。',
          '这也是短视频对城市最直接的影响：它生产了新的地标视角。',
        ],
      },
      {
        id: 's3',
        heading: '镜头之外的真实',
        paragraphs: [
          '我们尽量在每条条目旁边补一句当地生活的注脚，提醒读者画面只是切片，不是全部。',
          '这份精选希望你在被画面打动之后，也愿意去看看镜头之外的地方。',
        ],
      },
    ],
  },
];